import { Box, Button } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { AIS_DEFAULT_MAP_COLOR, IAisVesselFormValues, resolveAisMapColor } from 'src/api/ais-vessels'
import { AppForm, FormSubmitButton, FormTextField } from 'src/components/FormCore'
import { FormGeometryColorField } from 'src/components/FormGeometryColorField'
import { useZodForm } from 'src/hooks/zod-form'

const MMSI_MIN = 100000000
const MMSI_MAX = 999999999
const HEX_COLOR = /^#[0-9A-Fa-f]{6}$/

interface AisVesselFormProps {
  initialValues?: Partial<IAisVesselFormValues>
  onSubmit: (values: IAisVesselFormValues, pop: () => void) => Promise<void>
  pop: () => void
  isUpdateForm?: boolean
  isLoading?: boolean
}

export const AisVesselForm = ({
  initialValues,
  onSubmit,
  pop,
  isUpdateForm = false,
  isLoading = false,
}: AisVesselFormProps) => {
  const { t: translate } = useTranslation(['ais-vessel', 'common'])
  const defaultColor = resolveAisMapColor(initialValues?.color ?? AIS_DEFAULT_MAP_COLOR)

  const schema = z.object({
    mmsi: z
      .union([z.string(), z.number()])
      .transform(value => parseInt(String(value), 10))
      .refine(value => Number.isInteger(value) && value >= MMSI_MIN && value <= MMSI_MAX, {
        message: translate('mmsiInvalid', { ns: 'ais-vessel' }),
      }),
    name: z
      .string()
      .min(1, translate('nameRequired', { ns: 'ais-vessel' }))
      .max(200),
    callSign: z.string().max(50).optional().or(z.literal('')),
    color: z.string().regex(HEX_COLOR, translate('colorRequired', { ns: 'ais-vessel' })),
  })

  const form = useZodForm(schema, {
    defaultValues: {
      mmsi: initialValues?.mmsi ?? '',
      name: initialValues?.name ?? '',
      callSign: initialValues?.callSign ?? '',
      color: defaultColor,
    },
  })

  const handleSubmit = async (values: IAisVesselFormValues) => {
    await onSubmit(values, pop)
  }

  return (
    <AppForm form={form} onSubmit={handleSubmit} hasInitialValues={isUpdateForm}>
      <FormTextField name="mmsi" label={translate('mmsi', { ns: 'ais-vessel' })} />
      <FormTextField name="name" label={translate('boat-name', { ns: 'ais-vessel' })} />
      <FormTextField name="callSign" label={translate('call-sign', { ns: 'ais-vessel' })} />
      <FormGeometryColorField
        name="color"
        label={translate('color', { ns: 'ais-vessel' })}
        defaultValue={defaultColor}
      />
      <Box display="flex" flexDirection="row" justifyContent="end">
        <FormSubmitButton
          createText={translate('add', { ns: 'common' })}
          updateText={translate('update', { ns: 'common' })}
          isLoading={isLoading}
        />
        <Button onClick={pop}>{translate('cancel', { ns: 'common' })}</Button>
      </Box>
    </AppForm>
  )
}

export default AisVesselForm
